import { RendererContract } from '../RendererContract.js';

if (typeof window !== 'undefined') {
    import('./scss/homebrewed.scss');
}

const TEXT_STYLE_TAGS = Object.freeze({
    bold: 'strong',
    italic: 'em',
    underline: 'u',
    strikethrough: 's',
    code: 'code',
    mark: 'mark',
    small: 'small',
    sup: 'sup',
    sub: 'sub',
    deleted: 'del',
    inserted: 'ins',
});

export class HomeBrewedRenderer extends RendererContract {

    constructor(options = {}) {

        super(options);

        const {
            elementRenderer = null,
            iconRenderer = null,
        } = options;

        if (!elementRenderer)
            throw new Error(`[${this.constructor.name}] "options.elementRenderer" is required.`);
        if (!iconRenderer)
            throw new Error(`[${this.constructor.name}] "options.iconRenderer" is required.`);

        this.elementRenderer = elementRenderer;
        this.iconRenderer = iconRenderer;

    }

    getElementRenderer() {
        return this.elementRenderer;
    }

    getIconRenderer() {
        return this.iconRenderer;
    }

    el(tag, css = null, props = null, content = null) {
        return this.elementRenderer.renderElement(tag, this._normalizeCss(css), props, content);
    }

    createView(renderFactory, data = null) {
        return this.elementRenderer.createView(renderFactory, data);
    }

    injectView(view, data = null) {
        return this.elementRenderer.injectView(view, data);
    }

    link(options = null, content = null) {

        const url = typeof options?.onUrlGet === 'function'
            ? options.onUrlGet()
            : options?.url ?? options?.props?.href ?? null;

        return this.el('a', options?.css ?? null, {
            ...(options?.props ?? {}),
            href: url,
        }, content);

    }

    textStyle(style, text, options = null) {

        const styles = Array.isArray(style)
            ? style
            : String(style ?? '').split(/\s+/g).filter(Boolean);

        return styles.reduceRight((content, styleName) => {
            const tag = TEXT_STYLE_TAGS[styleName] ?? styleName;
            return this.el(tag, options?.css ?? null, options?.props ?? null, content);
        }, text);

    }

    heading(level, text, options = null) {

        const tag = this._headingTag(level);
        return this.el(tag, options?.css ?? null, options?.props ?? null, text);

    }

    app(options = null, content = null) { return this._layout('div', 'app', options, content); }

    main(options = null, content = null) { return this._layout('main', null, options, content); }

    module(options = null, content = null) { return this._layout('main', null, options, content); }

    header(options = null, content = null) { return this._layout('header', null, options, content); }

    footer(options = null, content = null) { return this._layout('footer', null, options, content); }

    section(options = null, content = null) { return this._layout('section', null, options, content); }

    aside(options = null, content = null) { return this._layout('aside', null, options, content); }

    card(options = null, content = null) { return this._layout('article', null, options, content); }

    row(options = null, content = null) { return this._layout('div', 'row', options, content); }

    dialog(options = null, content = null) {
        return this.div([
            this._layout('div', 'overlay', { active: options.active }),
            this._layout('dialog', 'dialog', options, content),
        ]);
    }

    group(options = null, content = null) { return this._layout('div', 'group', options, content); }

    grid(options = null, content = null) { return this._layout('div', 'grid', options, content); }

    cell(options = null, content = null) { return this._layout('div', 'cell', options, content); }

    stack(options = null, content = null) { return this._layout('div', 'stack', options, content); }

    spacer(options = null) { return this._layout('span', 'spacer', options, null); }

    divider(options = null) { return this._layout('hr', 'divider', options, null); }

    list(options = null, content = null) {
        return this.el('ul', options?.css ?? null, options?.props ?? null, this._renderCollectionContent('listItem', content, options));
    }

    listItem(options = null, content = null) {
        return this.el('li', options?.css ?? null, this._stateProps(options), content ?? options?.content ?? options?.label ?? null);
    }

    nav(options = null, content = null) {
        return this.el('nav', options?.css ?? null, options?.props ?? null, this._renderNavigationContent('navItem', 'nav', content, options));
    }

    navItem(label = null, icon = null, options = null, subNav = null, content = null) {
        return this._navigationItem('span', label, icon, options, subNav, content);
    }

    menu(options = null, content = null) {
        return this.el('menu', options?.css ?? null, options?.props ?? null, this._renderNavigationContent('menuItem', 'menu', content, options));
    }

    menuItem(label = null, icon = null, options = null, subMenu = null, content = null) {
        return this.el('li', options?.css ?? null, this._stateProps(options), [
            this._navigationItem('a', label, icon, options, null, content),
            subMenu,
        ].filter((item) => item !== null && item !== undefined && item !== ''));
    }

    breadcrumbs(options = null, content = null) {
        return this.el('nav', this._joinCss('breadcrumbs', options?.css), options?.props ?? null, [
            this.el('ol', null, null, this._renderNavigationContent('breadcrumbItem', 'breadcrumbs', content, options)),
        ]);
    }

    breadcrumbItem(label = null, icon = null, options = null, content = null) {
        return this.el('li', options?.css ?? null, this._stateProps(options), [
            this._navigationItem('span', label, icon, options, null, content),
        ]);
    }

    table(options = null, content = null) {

        if (content !== null && content !== undefined) {
            return this.el('table', options?.css ?? null, options?.props ?? null, content);
        }

        if (this._hasTableCallbacks(options)) {
            return this.el('table', options?.css ?? null, options?.props ?? null, this._renderCallbackTable(options));
        }

        const columns = this._visibleColumns(options?.columns ?? []);
        const items = Array.isArray(options?.items) ? options.items : [];
        const header = this.thead([
            this.trow(columns.map((column) => this.thcell({ css: this._columnCss(column), props: this._columnProps(column) }, [column.label ?? column.key ?? '']))),
        ]);
        const bodyRows = items.map((item) => this.trow(columns.map((column) => this.tcell(this._columnCss(column), [item?.[column.key] ?? '']))));
        const body = this.tbody(bodyRows.length ? bodyRows : [this._emptyTableRow(columns, options)]);

        return this.el('table', options?.css ?? null, options?.props ?? null, [header, body]);

    }

    button(label = null, icon = null, options = null, content = null) {

        const body = content ?? [
            this._renderIcon(icon, options?.iconOptions ?? null),
            label,
        ].filter((item) => item !== null && item !== undefined && item !== '');

        return this.el('button', options?.css ?? null, options?.props ?? null, body);

    }

    paginator(recordCount = 0, limit = 25, offset = 0, options = null) {

        if (options?.visible === false) return null;

        const state = this._normalizePaginatorState(recordCount, limit, offset);
        const pageLimits = this._normalizePageLimits(options?.pageLimits, state.limit);
        const disabled = options?.disabled === true;
        // const labels = {
        //     first: 'First',
        //     previous: 'Previous',
        //     next: 'Next',
        //     last: 'Last',
        //     status: this._formatPaginatorStatus(state),
        //     ...(options?.labels ?? {}),
        // };
        const labels = {
            first: null,
            previous: null,
            next: null,
            last: null,
            status: this._formatPaginatorStatus(state),
            ...(options?.labels ?? {}),
        };
        const icons = {
            first: '#fast-backward',
            previous: '#backward',
            next: '#forward',
            last: '#fast-forward',
            ...(options?.icons ?? {}),
        };
        const isFirst = state.pageIndex <= 0;
        const isLast = state.pageIndex >= state.pageCount - 1;
        const items = [];

        if (options?.showFirst !== false) items.push(this._renderPaginatorButton(labels.first, icons.first, disabled || isFirst, state, 0, options, 'paginator__first'));
        if (options?.showPrevious !== false) items.push(this._renderPaginatorButton(labels.previous, icons.previous, disabled || isFirst, state, Math.max(0, state.pageIndex - 1), options, 'paginator__previous'));
        if (options?.showStatus !== false) items.push(this.span('paginator__status', [labels.status]));
        if (options?.showNext !== false) items.push(this._renderPaginatorButton(labels.next, icons.next, disabled || isLast, state, Math.min(state.pageCount - 1, state.pageIndex + 1), options, 'paginator__next'));
        if (options?.showLast !== false) items.push(this._renderPaginatorButton(labels.last, icons.last, disabled || isLast, state, Math.max(0, state.pageCount - 1), options, 'paginator__last'));

        if (options?.showLimit !== false) {
            items.push(this.comboBox(state.limit, {
                css: 'paginator__limit',
                props: {
                    disabled: disabled ? 'disabled' : null,
                    onchange: disabled ? null : (event) => this._setPaginatorValue({ ...state, limit: Number(event?.target?.value) || state.limit, offset: 0 }, event, options),
                },
                items: pageLimits.map((value) => ({ value, label: String(value) })),
            }));
        }

        return this.nav({ css: this._joinCss('paginator', options?.css), props: options?.props ?? null }, items);

    }

    uploadButton(label = null, icon = null, options = null, content = null) {

        const props = { ...(options?.props ?? {}) };
        const originalOnClick = props.onclick;
        const disabled = options?.disabled === true || props.disabled === true || props.disabled === 'disabled';

        props.disabled = disabled ? 'disabled' : props.disabled ?? null;
        props.onclick = disabled ? null : async (event) => {
            if (typeof originalOnClick === 'function') originalOnClick(event);
            if (event?.defaultPrevented) return;
            const files = await this._pickFiles(options ?? {}, event);
            if (typeof options?.onValueSet === 'function') {
                options.onValueSet(files, event, { renderer: this, options });
            }
        };

        return this.button(label, icon, { ...(options ?? {}), props }, content);

    }

    dropdownButton(label = null, icon = null, options = null, content = null) {

        const props = { ...(options?.props ?? {}) };
        const originalOnClick = props.onclick;
        const disabled = options?.disabled === true || props.disabled === true || props.disabled === 'disabled';
        const expanded = options?.expanded === true;
        const autoclose = options?.autoclose === true || options?.closeOnItemClick === true;
        const anchorCss = this._dropdownButtonAnchorCss(options?.anchor);
        const dropdownContent = content ?? this._renderNavigationContent('menuItem', 'menu', null, options);
        const menu = this.menu({
            css: this._joinCss('dropdown dropdown-button__menu', options?.menuCss, expanded ? 'active' : null),
            props: {
                'data-autoclose': autoclose ? 'true' : null,
                onclick: autoclose ? (event) => this._closeDropdownButton(event, options ?? {}) : null,
            },
        }, dropdownContent);

        props.disabled = disabled ? 'disabled' : props.disabled ?? null;
        props.onclick = disabled ? null : (event) => {
            if (typeof originalOnClick === 'function') originalOnClick(event);
            if (event?.defaultPrevented) return;
            this._toggleDropdownButton(event, options ?? {});
        };

        const button = this.button(label, icon, {
            ...(options ?? {}),
            css: options?.buttonCss ?? null,
            props,
        }, [
            label,
            this._renderIcon(icon),
        ].filter((item) => item !== null && item !== undefined && item !== ''));

        return this.el('div', this._joinCss('dropdown-button', anchorCss, expanded ? 'active' : null, options?.css), null, [button, menu]);

    }

    field(options = null, content = null) {

        if (options?.visible === false) return null;

        const invalid = options?.invalid === true || options?.error !== null && options?.error !== undefined && options?.error !== '';
        const props = {
            ...(options?.props ?? {}),
            'data-required': options?.required ? 'true' : null,
            'data-disabled': options?.disabled ? 'true' : null,
            'data-readonly': options?.readOnly ? 'true' : null,
            'aria-invalid': invalid ? 'true' : options?.props?.['aria-invalid'] ?? null,
        };

        return this.el('div', this._joinCss('field', invalid ? 'invalid' : null, options?.css), props, [
            options?.label !== null && options?.label !== undefined ? this.label({ css: 'field-label', props: options?.controlId ? { for: options.controlId } : null }, [options.label]) : null,
            content,
            options?.hint !== null && options?.hint !== undefined ? this.el('div', 'field-hint', null, options.hint) : null,
            this._inputError(options?.error ?? null),
        ]);

    }

    textBox(value = null, options = null) {

        const resolvedValue = this._resolveValue(value, options);
        const text = this._formatValue(resolvedValue, options);
        const props = { ...(options?.props ?? {}), value: text ?? '' };
        const eventName = options?.multiline ? 'oninput' : 'onchange';
        const existingHandler = props[eventName];

        if (typeof options?.onValueSet === 'function') {
            props[eventName] = (event) => {
                const parsedValue = this._parseText(event?.target?.value ?? '', options);
                options.onValueSet(parsedValue, event);
                if (typeof existingHandler === 'function') existingHandler(event);
            };
        }

        return options?.multiline
            ? this.el('textarea', options?.css ?? null, props, text ?? '')
            : this.el('input', options?.css ?? null, { type: 'text', ...props }, null);

    }

    slider(value = null, options = null) {

        const resolvedValue = this._resolveValue(value, options);
        const props = {
            ...(options?.props ?? {}),
            type: 'range',
            value: resolvedValue ?? '',
            min: options?.min ?? options?.props?.min ?? null,
            max: options?.max ?? options?.props?.max ?? null,
            step: options?.step ?? options?.props?.step ?? null,
        };
        const existingOnChange = props.onchange;

        if (typeof options?.onValueSet === 'function') {
            props.onchange = (event) => {
                const nextValue = Number(event?.target?.value);
                options.onValueSet(Number.isNaN(nextValue) ? null : nextValue, event);
                if (typeof existingOnChange === 'function') existingOnChange(event);
            };
        }

        return this.el('input', options?.css ?? null, props, null);

    }

    progress(value = null, options = null) {

        const resolvedValue = this._resolveValue(value, options);
        const props = {
            ...(options?.props ?? {}),
            value: options?.indeterminate ? null : resolvedValue ?? null,
            min: options?.min ?? options?.props?.min ?? null,
            max: options?.max ?? options?.props?.max ?? null,
        };
        const progress = this.el('progress', options?.css ?? null, props, options?.indeterminate ? null : this._formatValue(resolvedValue, options));

        if (options?.label === null || options?.label === undefined) return progress;
        return this.el('span', 'progress-wrapper', null, [progress, options.label]);

    }

    checkBox(value = false, label = null, options = null, content = null) {

        const resolvedValue = Boolean(this._resolveValue(value, options));
        const props = {
            ...(options?.props ?? {}),
            type: 'checkbox',
            checked: resolvedValue ? 'checked' : null,
        };
        const existingOnChange = props.onchange;

        if (typeof options?.onValueSet === 'function') {
            props.onchange = (event) => {
                options.onValueSet(Boolean(event?.target?.checked), event);
                if (typeof existingOnChange === 'function') existingOnChange(event);
            };
        }

        const input = this.el('input', null, props, null);
        const body = content ?? [input, label].filter((item) => item !== null && item !== undefined && item !== '');

        return label !== null && label !== undefined || content !== null && content !== undefined
            ? this.el('label', options?.css ?? null, null, body)
            : this.el('span', options?.css ?? null, null, [input]);

    }

    checkGroup(value = [], options = null, content = null) {

        if (content !== null && content !== undefined) {
            return this.el('div', options?.css ?? null, options?.props ?? null, content);
        }

        const selectedValues = Array.isArray(this._resolveValue(value, options)) ? this._resolveValue(value, options) : [];
        const items = this._choiceItems(options).map((item) => this.checkBox(selectedValues.some((selected) => this._sameValue(selected, item.value)), item.label ?? item.value, {
            props: { disabled: item.disabled ? 'disabled' : null },
            onValueSet: (checked, event) => {
                if (typeof options?.onValueSet !== 'function') return;
                const nextValues = checked
                    ? [...selectedValues, item.value]
                    : selectedValues.filter((selected) => !this._sameValue(selected, item.value));
                options.onValueSet(nextValues, item, event);
            },
        }));

        return this.el('div', options?.css ?? null, options?.props ?? null, items);

    }

    radioGroup(name, value = null, options = null, content = null) {

        if (content !== null && content !== undefined) {
            return this.el('div', options?.css ?? null, options?.props ?? null, content);
        }

        const selectedValue = this._resolveValue(value, options);
        const items = this._choiceItems(options, true).map((item) => {
            const props = {
                type: 'radio',
                name,
                value: item.value ?? '',
                checked: this._sameValue(selectedValue, item.value) ? 'checked' : null,
                disabled: item.disabled ? 'disabled' : null,
            };
            if (typeof options?.onValueSet === 'function') {
                props.onchange = (event) => options.onValueSet(item.value, item, event);
            }
            return this.el('label', null, null, [this.el('input', null, props, null), item.label ?? item.value ?? '']);
        });

        return this.el('div', options?.css ?? null, options?.props ?? null, items);

    }

    comboBox(value = null, options = null, content = null) {

        const resolvedValue = this._resolveValue(value, options);
        const props = { ...(options?.props ?? {}), value: resolvedValue ?? '' };
        const existingOnChange = props.onchange;

        if (typeof options?.onValueSet === 'function') {
            props.onchange = (event) => {
                const selectedValue = event?.target?.value;
                const item = (options?.items ?? []).find((candidate) => this._sameValue(candidate?.value, selectedValue)) ?? null;
                options.onValueSet(selectedValue, item, event);
                if (typeof existingOnChange === 'function') existingOnChange(event);
            };
        }

        return this.el('select', options?.css ?? null, props, content ?? this._renderComboBoxOptions(resolvedValue, options));

    }

    empty(options = null, content = null) {

        if (options?.visible === false) return null;

        const body = content ?? [
            this._renderIcon(options?.icon ?? null, 'empty__icon'),
            options?.title !== null && options?.title !== undefined ? this.el('strong', 'empty__title', null, options.title) : null,
            options?.subtitle !== null && options?.subtitle !== undefined ? this.el('span', 'empty__subtitle', null, options.subtitle) : null,
        ].filter((item) => item !== null && item !== undefined && item !== '');

        return this.el('div', this._joinCss('empty', options?.type ? `empty--${options.type}` : null, options?.css), options?.props ?? null, body.length ? body : null);

    }

    _layout(tag, baseCss, options = null, content = null) {

        if (options?.visible === false) return null;
        return this.el(
            tag,
            this._joinCss(
                baseCss,
                this._layoutModifier('position', options?.position),
                this._layoutModifier('size', options?.size),
                this._layoutModifier('active', options?.active ? 'active' : null),
                this._layoutModifier('width', options?.width),
                this._layoutModifier('height', options?.height),
                this._layoutModifier('padding', options?.padding),
                this._layoutModifier('margin', options?.margin),
                options?.css
            ),
            options?.props ?? null,
            content
        );

    }

    _layoutModifier(name, value) {
        if (value === null || value === undefined)
            return null;
        if (Array.isArray(value))
            return value.join(' ');
        return value ? String(value) : null;
    }

    _headingTag(level) {

        const headingLevel = Number(level);
        if (!Number.isInteger(headingLevel) || headingLevel < 1 || headingLevel > 6) {
            throw new RangeError(`[${this.constructor.name}] heading level must be an integer from 1 to 6.`);
        }
        return `h${headingLevel}`;

    }

    _navigationItem(defaultTag, label = null, icon = null, options = null, subNavigation = null, content = null) {

        const tag = options?.url ? 'a' : defaultTag;
        const props = {
            ...(options?.props ?? {}),
            href: options?.url ?? options?.props?.href ?? null,
            disabled: options?.disabled ? 'disabled' : options?.props?.disabled ?? null,
            onclick: options?.disabled ? null : options?.onClick ?? options?.props?.onclick ?? null,
        };
        const body = content ?? [this._renderIcon(icon), label, subNavigation].filter((item) => item !== null && item !== undefined && item !== '');

        return this.el(tag, options?.css ?? null, props, body);

    }

    _renderNavigationContent(itemHelperName, childCollectionName, content = null, options = null) {

        if (content !== null && content !== undefined) return content;

        if (typeof options?.onItemCount === 'function' && typeof options?.onItemRender === 'function') {
            return Array.from({ length: options.onItemCount() }, (_, index) => options.onItemRender(this, index));
        }

        if (!Array.isArray(options?.items)) return null;

        return options.items.map((item) => {
            const childItems = Array.isArray(item?.items) && item.items.length
                ? this[childCollectionName]({ items: item.items })
                : null;
            return this[itemHelperName](item?.label ?? null, item?.icon ?? null, item, childItems, item?.content ?? null);
        });

    }

    _stateProps(options = null) {
        return {
            ...(options?.props ?? {}),
            disabled: options?.disabled ? 'disabled' : options?.props?.disabled ?? null,
        };
    }

    _renderIcon(icon = null, cssOrOptions = null) {

        if (icon === null || icon === undefined || icon === '') return null;
        if (typeof icon === 'string') return this.icon(icon, cssOrOptions);
        return icon;

    }

    _hasTableCallbacks(options = null) {
        return typeof options?.onRowRender === 'function' || typeof options?.onCellRender === 'function';
    }

    _renderCallbackTable(options = null) {

        const sections = [];
        for (const context of ['head', 'body', 'foot']) {
            const rowCount = typeof options?.onRowCount === 'function' ? options.onRowCount(context) : 0;
            if (rowCount <= 0) continue;
            const tag = context === 'head' ? 'thead' : context === 'foot' ? 'tfoot' : 'tbody';
            sections.push(this.el(tag, null, null, Array.from({ length: rowCount }, (_, rowIndex) => this._renderCallbackTableRow(options, context, rowIndex))));
        }

        if (!sections.some((section) => section?.tag === 'tbody') && typeof options?.onEmptyContentRender === 'function') {
            sections.push(this.tbody([this.trow([this.tcell([options.onEmptyContentRender(this)])])]));
        }

        return sections;

    }

    _renderCallbackTableRow(options, context, rowIndex) {

        const item = context === 'body' && Array.isArray(options?.items) ? options.items[rowIndex] ?? null : null;
        if (typeof options?.onRowRender === 'function') return options.onRowRender(this, context, rowIndex, item);

        const colCount = typeof options?.onColCount === 'function' ? options.onColCount(context) : 0;
        return this.trow(Array.from({ length: colCount }, (_, colIndex) => options.onCellRender(this, context, rowIndex, colIndex, item, null)));

    }

    _visibleColumns(columns = []) {
        return columns.filter((column) => column?.visible !== false);
    }

    _columnCss(column = null) {
        return column?.align ? `text-${column.align}` : null;
    }

    _columnProps(column = null) {
        return column?.width !== null && column?.width !== undefined ? { style: `width: ${column.width}` } : null;
    }

    _emptyTableRow(columns = [], options = null) {
        const emptyContent = typeof options?.onEmptyContentRender === 'function'
            ? options.onEmptyContentRender(this)
            : '';
        return this.trow([this.tcell({ props: { colspan: Math.max(1, columns.length) } }, [emptyContent])]);
    }

    _inputError(error = null) {
        if (!error) return null;
        return this.el('div', 'input-error', null, error);
    }

    _resolveValue(value = null, options = null) {
        return typeof options?.onValueGet === 'function' ? options.onValueGet() : value;
    }

    _formatValue(value = null, options = null) {
        return typeof options?.onFormatValue === 'function' ? options.onFormatValue(value) : value;
    }

    _parseText(text = '', options = null) {
        return typeof options?.onParseText === 'function' ? options.onParseText(text) : text;
    }

    _choiceItems(options = null, includeEmpty = false) {

        const items = [];
        if (includeEmpty && options?.emptyNotDisplayed !== true) {
            items.push({ value: options?.emptyValue ?? null, label: options?.emptyLabel ?? '' });
        }

        if (typeof options?.onItemCount === 'function' && typeof options?.onItemRender === 'function') {
            return Array.from({ length: options.onItemCount() }, (_, index) => options.onItemRender(this, index));
        }

        return items.concat(options?.items ?? []);

    }

    _renderComboBoxOptions(value = null, options = null) {

        if (typeof options?.onItemCount === 'function' && typeof options?.onItemRender === 'function') {
            return Array.from({ length: options.onItemCount() }, (_, index) => options.onItemRender(this, index));
        }

        const items = [];
        if (options?.emptyNotDisplayed !== true) {
            const emptyValue = options?.emptyValue ?? '';
            items.push(this.option({
                props: {
                    value: emptyValue,
                    selected: this._sameValue(value, emptyValue) ? 'selected' : null,
                },
            }, [options?.emptyLabel ?? '']));
        }

        for (const item of options?.items ?? []) {
            items.push(this.option({
                props: {
                    value: item?.value ?? '',
                    disabled: item?.disabled ? 'disabled' : null,
                    selected: this._sameValue(value, item?.value) ? 'selected' : null,
                },
            }, [item?.label ?? item?.value ?? '']));
        }

        return items;

    }

    _sameValue(left, right) {
        return String(left ?? '') === String(right ?? '');
    }

    _normalizePaginatorState(recordCount = 0, limit = 25, offset = 0) {

        const normalizedRecordCount = Math.max(0, Number(recordCount) || 0);
        const normalizedLimit = Math.max(1, Number(limit) || 25);
        const maxOffset = Math.max(0, (Math.ceil(normalizedRecordCount / normalizedLimit) - 1) * normalizedLimit);
        const normalizedOffset = Math.min(maxOffset, Math.max(0, Number(offset) || 0));
        const pageCount = Math.max(1, Math.ceil(normalizedRecordCount / normalizedLimit));
        const pageIndex = Math.min(pageCount - 1, Math.floor(normalizedOffset / normalizedLimit));

        return {
            recordCount: normalizedRecordCount,
            limit: normalizedLimit,
            offset: pageIndex * normalizedLimit,
            pageIndex,
            pageCount,
        };

    }

    _normalizePageLimits(pageLimits = null, currentLimit = 25) {

        const values = Array.isArray(pageLimits) ? pageLimits : [10, 25, 50, 100];
        return [...new Set([...values, currentLimit]
            .map((value) => Number(value))
            .filter((value) => Number.isInteger(value) && value > 0))]
            .sort((a, b) => a - b);

    }

    _formatPaginatorStatus(state) {

        if (state.recordCount <= 0) return `0 of 0 (page 1 of ${state.pageCount})`;
        const first = Math.min(state.recordCount, state.offset + 1);
        const last = Math.min(state.recordCount, state.offset + state.limit);
        return `${first}-${last} of ${state.recordCount} (page ${state.pageIndex + 1} of ${state.pageCount})`;

    }

    _renderPaginatorButton(label, icon, disabled, state, targetPageIndex, options, css) {

        return this.button(label, icon, {
            css,
            props: {
                disabled: disabled ? 'disabled' : null,
                onclick: disabled ? null : (event) => this._setPaginatorValue({ ...state, offset: targetPageIndex * state.limit }, event, options),
            },
        });

    }

    _setPaginatorValue(value, event, options = null) {

        const state = this._normalizePaginatorState(value.recordCount, value.limit, value.offset);
        if (typeof options?.onValueSet === 'function') {
            options.onValueSet(state, event, { renderer: this, options });
        }

    }

    _pickFiles(options = {}, event = null) {

        if (typeof document === 'undefined' || typeof document.createElement !== 'function') {
            throw new Error(`[${this.constructor.name}] document.createElement is required for uploadButton().`);
        }

        return new Promise((resolve) => {
            const input = document.createElement('input');
            input.type = 'file';
            input.multiple = options.multiple === true;
            input.accept = options.accept ?? '';
            if (options.name) input.name = options.name;

            input.addEventListener('change', () => {
                const files = Array.from(input.files ?? []);
                if (options.resetAfterChange !== false) input.value = '';
                resolve(files);
            });

            input.click();
        });

    }

    _toggleDropdownButton(event, options = {}) {

        const buttonNode = this._findAncestorByNodeName(event?.target, 'BUTTON');
        const hostNode = this._findAncestorByCssClass(buttonNode, 'dropdown-button') ?? buttonNode?.parentNode ?? null;
        const menuNode = this._findChildByNodeName(hostNode, 'MENU');
        const opening = !(menuNode?.classList?.contains?.('active') ?? options.expanded === true);

        if (opening) {
            menuNode?.classList?.add?.('active');
            hostNode?.classList?.add?.('active');
            if (typeof options.onValueSet === 'function') options.onValueSet(true, event, { renderer: this, options });
            if (typeof options.onOpen === 'function') options.onOpen({ renderer: this, options });
        } else {
            this._closeDropdownButton(event, options, hostNode, menuNode);
        }

        if (opening && hostNode && menuNode && typeof document !== 'undefined' && typeof document.addEventListener === 'function') {
            event?.stopPropagation?.();
            const outsideClickListener = (outsideEvent) => {
                if (!hostNode.contains(outsideEvent.target)) {
                    this._closeDropdownButton(outsideEvent, options, hostNode, menuNode);
                    document.removeEventListener('click', outsideClickListener);
                }
            };
            setTimeout(() => document.addEventListener('click', outsideClickListener), 0);
        }

    }

    _closeDropdownButton(event, options = {}, hostNode = null, menuNode = null) {

        const resolvedHostNode = hostNode ?? this._findAncestorByCssClass(event?.target, 'dropdown-button') ?? null;
        const resolvedMenuNode = menuNode ?? this._findChildByNodeName(resolvedHostNode, 'MENU');
        const wasActive = resolvedMenuNode?.classList?.contains?.('active') === true;

        resolvedMenuNode?.classList?.remove?.('active');
        resolvedHostNode?.classList?.remove?.('active');
        if (!wasActive) return;
        if (typeof options.onValueSet === 'function') options.onValueSet(false, event, { renderer: this, options });
        if (typeof options.onClose === 'function') options.onClose({ renderer: this, options });

    }

    _findAncestorByNodeName(node, nodeName) {

        const expected = String(nodeName).toUpperCase();
        let current = node;
        while (current) {
            if (String(current.nodeName).toUpperCase() === expected) return current;
            current = current.parentNode;
        }
        return null;

    }

    _findChildByNodeName(node, nodeName) {

        const expected = String(nodeName).toUpperCase();
        let current = node?.firstChild ?? null;
        while (current) {
            if (String(current.nodeName).toUpperCase() === expected) return current;
            current = current.nextSibling;
        }
        return null;

    }

    _findAncestorByCssClass(node, cssClass) {

        let current = node;
        while (current) {
            if (current.classList?.contains?.(cssClass)) return current;
            current = current.parentNode;
        }
        return null;

    }

    _dropdownButtonAnchorCss(anchor = null) {

        const tokens = String(anchor ?? 'bottom right').split(/\s+/g).filter(Boolean);
        const blockAnchor = tokens.includes('top') ? 'top' : 'bottom';
        const inlineAnchor = tokens.includes('left')
            ? 'left'
            : tokens.includes('stretch') || tokens.includes('content')
                ? 'stretch'
                : 'right';

        return `${blockAnchor}-anchor ${inlineAnchor}-anchor`;

    }

    _joinCss(...parts) {
        return parts.flatMap((part) => this._normalizeCss(part)?.split(/\s+/) ?? []).filter(Boolean).join(' ') || null;
    }

}
